import { createEntityAdapter, createSelector, createSlice, EntityState, PayloadAction } from '@reduxjs/toolkit'
import { RootState } from '../store'
import { IntegrationEmail, IntegrationEmailThread } from '../../models/Email'

interface EmailThreadsState {
  isLoading: boolean
  moreLoading: boolean
  hasMore: boolean
  emailThreads: EntityState<IntegrationEmailThread, string>
  initialized: boolean
}

const emailThreadsAdapter = createEntityAdapter<IntegrationEmailThread, string>({ selectId: (thread: IntegrationEmailThread) => thread.externalId })

const initialState: EmailThreadsState = {
  isLoading: true,
  moreLoading: false,
  hasMore: true,
  emailThreads: emailThreadsAdapter.getInitialState(),
  initialized: false,
}

export const emailThreadSlice = createSlice({
  name: 'emailThreads',
  initialState,
  reducers: {
    setEmailThreadsLoading: (state, action: PayloadAction<boolean>) => {
      state.isLoading = action.payload
    },
    setEmailThreadsMoreLoading: (state, action: PayloadAction<boolean>) => {
      state.moreLoading = action.payload
    },
    setEmailThreadsHasMore: (state, action: PayloadAction<boolean>) => {
      state.hasMore = action.payload
    },
    setEmailThreadsInitialized: (state) => {
      state.initialized = true
    },
    setEmailThreads: (state, action: PayloadAction<IntegrationEmailThread[]>) => {
      emailThreadsAdapter.setMany(state.emailThreads, action.payload)
    },
    clearEmailThreads: (state) => {
      emailThreadsAdapter.removeAll(state.emailThreads)
    },
    setEmailThread: (state, action: PayloadAction<IntegrationEmailThread>) => {
      emailThreadsAdapter.setOne(state.emailThreads, action.payload)
    },
    addEmailToThread: (state, action: PayloadAction<{ email: IntegrationEmail; threadId: string }>) => {
      const thread = emailThreadsAdapter.getSelectors().selectById(state.emailThreads, action.payload.threadId)
      if (!thread) {
        return
      }
      emailThreadsAdapter.setOne(state.emailThreads, { ...thread, emails: [...thread.emails, action.payload.email] })
    },
    resetEmailThreads: () => initialState,
  },
})

export const {
  setEmailThreadsLoading,
  setEmailThreadsInitialized,
  setEmailThreads,
  setEmailThreadsMoreLoading,
  setEmailThreadsHasMore,
  clearEmailThreads,
  setEmailThread,
  resetEmailThreads,
  addEmailToThread,
} = emailThreadSlice.actions

const emailThreadSelectors = emailThreadsAdapter.getSelectors((state: RootState) => state.emailHub.emailThreads.emailThreads)

export const selectEmailThreads = createSelector([emailThreadSelectors.selectAll], (emailThreads) => {
  return [...emailThreads]
    .filter((thread) => !!thread.emails.length)
    .sort((thread1: IntegrationEmailThread, thread2: IntegrationEmailThread) => {
      const lastEmail1 = thread1.emails.at(-1)
      const lastEmail2 = thread2.emails.at(-1)
      if (!lastEmail1 || !lastEmail2) {
        return 0
      }
      return lastEmail1.createdAt > lastEmail2.createdAt ? -1 : 1
    })
})

export const selectEmailThreadsLoading = (state: RootState) => state.emailHub.emailThreads.isLoading

export const selectEmailThreadsMoreLoading = (state: RootState) => state.emailHub.emailThreads.moreLoading

export const selectEmailThreadsHasMore = (state: RootState) => state.emailHub.emailThreads.hasMore

export const selectShouldInitialize = createSelector(
  [(state: RootState) => state.emailHub.emailThreads.initialized, selectEmailThreads, (_: RootState, threadId?: string) => threadId],
  (initialized, emailThreads, threadId) => {
    if (!threadId) {
      return !initialized
    }
    return !emailThreads.some((thread) => thread.externalId === threadId)
  },
)
